import { AttachmentMeta } from "@/indexedDB/types";
import { AlertCircle, Loader, Play, X } from "lucide-react";
import { CSSProperties, useMemo, useState } from "react";
import useMediaAttachment from "@/middleware/hooks/attachments/useMediaAttachment";
import useAutoDownload from "@/middleware/hooks/attachments/useAutoDownload";
import { useUploadProgressState } from "@/zustand/UploadProgressState";
import { useUploadControlState } from "@/zustand/UploadControlState";
import { getMediaAspect, placeholderToDataURL } from "@/utils/mediaPlaceholder";
import { fillBoxWidth, fitMediaBox } from "@/utils/mediaSizing";
import { isVideoType } from "@/utils/attachmentType";

// Layout knobs. The bubble fills its column up to MEDIA_FILL_WIDTH (matches
// MessageBubble's lg max-width) so the image is never narrower than the text —
// no empty gap — and rides the bubble out to near-full-width on mobile. Height
// follows the true aspect ratio, clamped so tall portraits letterbox (neutral
// bars) instead of dominating the screen and wide panoramas don't become slivers.
const MEDIA_FILL_WIDTH = 520;
const MAX_MEDIA_HEIGHT = 480;
const MIN_MEDIA_HEIGHT = 120;
// Floor for the capped box: a small image's long side never renders below this,
// so tiny images stay visible/tappable — while still never being blown up to
// the full fill width (which pixelates them).
const MIN_MEDIA_DISPLAY = 80;

type Props = {
  messageId: string;
  messageTimestamp: string;
  chatId: string;
  attachment: AttachmentMeta;
  /** Only the sender of an in-progress chunked upload gets the cancel button. */
  isOutgoing?: boolean;
};

/**
 * MediaAttachment component
 *
 * Purpose:
 * Displays media attachments (images and videos) in the message thread.
 *
 * Context:
 * Used within the message component to render attachments inline.
 * Handles loading states and provides play button overlay for videos.
 *
 * @param props - Component props
 * @returns React component
 */
export default function MediaAttachment({
  messageId,
  messageTimestamp,
  chatId,
  attachment,
  isOutgoing = false,
}: Props) {
  const { loading, attachmentUrl } = useMediaAttachment({
    messageId,
    chatId,
    attachment,
    messageTimestamp,
  });

  // Opt-in auto-download: when enabled in settings, warm the cache with the
  // full-size media as the bubble renders (default off → no-op).
  useAutoDownload({
    chatId,
    messageId,
    timestamp: messageTimestamp,
    attachment,
    kind: isVideoType(attachment.fileType) ? "video" : "image",
  });

  const uploadProgress = useUploadProgressState(
    (state) => state.progress[attachment.attachmentId],
  );
  const cancelUpload = useUploadControlState((state) => state.cancel);
  const isUploading = uploadProgress !== undefined;
  const isUploadFailed = attachment.uploadState?.status === "failed";

  // Aspect ratio from the embedded dimensions/placeholder. Legacy messages carry
  // neither → getMediaAspect returns null and we fall back to the thumbnail's own
  // natural ratio once it loads (a neutral square until then). The blurred
  // ThumbHash placeholder paints instantly, before the thumbnail is fetched.
  const { width, height, placeholder } = attachment;
  const knownAspect = useMemo(
    () => getMediaAspect({ width, height, placeholder }),
    [width, height, placeholder],
  );
  // Native pixel size, used to CAP the display box so a small image is never
  // scaled UP to fill the bubble (only ever down) — object-contain would
  // otherwise upscale it and it reads as pixelated. Exact embedded dims when
  // present, else read off the thumbnail once it paints (legacy messages, or
  // ones that carry only a ThumbHash aspect).
  const [natSize, setNatSize] = useState<{ w: number; h: number } | null>(null);
  const nativeSize = useMemo(
    () =>
      width && height && width > 0 && height > 0
        ? { w: width, h: height }
        : natSize,
    [width, height, natSize],
  );
  const aspect = nativeSize ? nativeSize.w / nativeSize.h : (knownAspect ?? 1);
  const placeholderUrl = useMemo(
    () => placeholderToDataURL(placeholder),
    [placeholder],
  );

  // Display box. Until the native size is known (legacy / ThumbHash-only,
  // pre-load) keep the fill box so it doesn't reflow jarringly; once known, cap
  // it at native (down to the fill limits, floored at MIN_MEDIA_DISPLAY).
  const boxStyle = useMemo<CSSProperties>(() => {
    if (!nativeSize) {
      // Only the ASPECT is known yet (a video — dimensions aren't embedded at
      // send, so the pixel size arrives with the thumbnail image). Derive the
      // width from the height limit: an explicit fill width + max-height clamps
      // the height but NOT the width, which rendered portrait videos as a
      // full-width landscape box that then snapped narrow on load. See
      // fillBoxWidth.
      return {
        width: fillBoxWidth(aspect, {
          fillWidth: MEDIA_FILL_WIDTH,
          maxHeight: MAX_MEDIA_HEIGHT,
        }),
        maxWidth: "100%",
        aspectRatio: aspect,
        maxHeight: MAX_MEDIA_HEIGHT,
        minHeight: MIN_MEDIA_HEIGHT,
      };
    }
    const { width: w } = fitMediaBox(nativeSize.w, nativeSize.h, {
      fillWidth: MEDIA_FILL_WIDTH,
      maxHeight: MAX_MEDIA_HEIGHT,
      minDisplay: MIN_MEDIA_DISPLAY,
    });
    // A LARGE image was downscaled to fill the column: keep the [MIN,MAX] height
    // clamp so an extreme panorama letterboxes (neutral bars) at
    // MIN_MEDIA_HEIGHT instead of collapsing to a sliver (a 20:1 image would be
    // ~26px tall otherwise). A SMALL image (capped below the fill width) must
    // NOT get a min-height — that would strand it in empty bars; it sits at its
    // native size and is centered (mx-auto).
    const wasDownscaled =
      nativeSize.w > MEDIA_FILL_WIDTH || nativeSize.h > MAX_MEDIA_HEIGHT;
    // width + aspectRatio (not an explicit height) keeps it responsive: on a
    // narrow column maxWidth:100% shrinks the width and the height follows.
    return {
      width: w,
      maxWidth: "100%",
      aspectRatio: aspect,
      ...(wasDownscaled
        ? { minHeight: MIN_MEDIA_HEIGHT, maxHeight: MAX_MEDIA_HEIGHT }
        : {}),
    };
  }, [nativeSize, aspect]);

  // r=18 → circumference = 2π×18 ≈ 113.1
  const CIRCUMFERENCE = 113.1;
  const strokeDashoffset = CIRCUMFERENCE * (1 - (uploadProgress ?? 0) / 100);

  return (
    <div
      // mx-auto centers the box when it's narrower than the bubble (a small
      // image whose caption widened the bubble) instead of stranding it at the
      // left; a no-op when the box already fills the column (large media).
      className="relative mx-auto overflow-hidden rounded-sm bg-muted"
      // Definite width (no collapse when the image is the bubble's only
      // content), capped at the image's native size so small media isn't blown
      // up, and to the column (max-width:100% + min-w-0 on the grid item lets
      // it shrink responsively on mobile). See boxStyle above.
      style={boxStyle}
    >
      {/* Instant blurred placeholder (bottom layer). Shows through until the
          thumbnail paints, then the thumbnail covers it. */}
      {placeholderUrl && (
        <img
          className="absolute inset-0 size-full scale-105 object-cover blur-md"
          src={placeholderUrl}
          alt=""
          aria-hidden="true"
          draggable={false}
        />
      )}
      {/* Loading shimmer only when there is no placeholder to show (legacy). */}
      {loading && !placeholderUrl && (
        <div className="absolute inset-0 z-10 flex size-full animate-pulse items-center justify-center rounded-sm bg-muted align-middle">
          <Loader className="size-7 animate-spin text-secondary" />
        </div>
      )}
      {attachmentUrl && (
        <img
          className="ios-image-safe absolute inset-0 size-full touch-manipulation select-none object-contain"
          src={attachmentUrl}
          alt={attachment.filename}
          draggable={false}
          onLoad={
            // No exact embedded dims (legacy / ThumbHash-only): read the real
            // size off the thumbnail once it paints, so the bubble reshapes to
            // the true aspect AND caps at the native size.
            !(width && height)
              ? (e) => {
                  const { naturalWidth, naturalHeight } = e.currentTarget;
                  if (naturalWidth > 0 && naturalHeight > 0) {
                    setNatSize({ w: naturalWidth, h: naturalHeight });
                  }
                }
              : undefined
          }
        />
      )}
      {isUploading && (
        <div className="absolute left-0 top-0 z-10 flex aspect-auto size-full items-center justify-center rounded-sm bg-black/40">
          <div className="relative flex items-center justify-center">
            <svg className="size-12 -rotate-90" viewBox="0 0 44 44" fill="none">
              <circle
                cx="22"
                cy="22"
                r="18"
                stroke="white"
                strokeOpacity="0.3"
                strokeWidth="3"
              />
              <circle
                cx="22"
                cy="22"
                r="18"
                className="stroke-primary/20 dark:stroke-primary"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={CIRCUMFERENCE}
                strokeDashoffset={strokeDashoffset}
                style={{ transition: "stroke-dashoffset 0.3s ease" }}
              />
            </svg>
            {isOutgoing && (
              // Center cancel button (Telegram-style). Sender only — recipients
              // can't cancel someone else's upload. stopPropagation so the click
              // cancels instead of bubbling to the parent's open-preview handler.
              <button
                type="button"
                aria-label="Cancel upload"
                onClick={(e) => {
                  e.stopPropagation();
                  cancelUpload(attachment.attachmentId);
                }}
                className="absolute inset-0 m-auto flex size-11 items-center justify-center rounded-full text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                <X className="size-4" />
              </button>
            )}
          </div>
        </div>
      )}
      {isUploadFailed && (
        <div className="absolute left-0 top-0 z-10 flex aspect-auto size-full items-center justify-center rounded-sm bg-black/50">
          <div className="flex flex-col items-center gap-1">
            <AlertCircle className="size-7 text-destructive" />
            <span className="text-xs text-white">Upload failed</span>
          </div>
        </div>
      )}
      {attachment.fileType.startsWith("video/") &&
        attachmentUrl &&
        !isUploading &&
        !isUploadFailed && (
          <div className="z-5 absolute left-0 top-0 flex aspect-auto size-full items-center justify-center rounded-sm align-middle">
            <div className="flex items-center justify-center rounded-full bg-primary/30 p-1.5">
              <Play className="size-7" />
            </div>
          </div>
        )}
    </div>
  );
}
